import * as React from "react";
import {AriaButtonOptions, useButton} from "@react-aria/button";
import {useFocusRing} from "@react-aria/focus";
import {useHover} from "@react-aria/interactions";
import clsx from "clsx";

interface IconButtonProps extends AriaButtonOptions<"button"> {
    children: React.ReactNode;
    variant?: 'outlined' | 'contained' | 'embedded' | 'ghost';
    color?: 'primary' | 'secondary' | 'success' | 'error';
    size?: 'small' | 'medium' | 'large';
    loading?: boolean;
    className?: string;
    isDisabled?: boolean;
    "aria-label": string; // Required for accessibility
}

export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
    (props, forwardedRef) => {
        const {
            children,
            variant = 'ghost',
            color = 'primary',
            size = 'medium',
            loading,
            className,
            isDisabled,
            ...rest
        } = props;

        const internalRef = React.useRef<HTMLButtonElement | null>(null);

        // Sync forwarded ref with internal ref
        React.useImperativeHandle(forwardedRef, () => internalRef.current!);

        const {buttonProps, isPressed} = useButton(
            {...rest, isDisabled},
            internalRef
        );
        const {isFocused, isFocusVisible, focusProps} = useFocusRing();
        const {hoverProps, isHovered} = useHover({isDisabled});

        // Size classes - icon buttons are square
        const sizeClasses = {
            small: 'w-7 h-7 p-1',
            medium: 'w-9 h-9 p-2',
            large: 'w-11 h-11 p-2.5'
        };

        // Icon size classes to ensure proper icon sizing
        const iconSizeClasses = {
            small: '[&>svg]:w-4 [&>svg]:h-4',
            medium: '[&>svg]:w-5 [&>svg]:h-5',
            large: '[&>svg]:w-6 [&>svg]:h-6'
        };

        // Base classes for all icon buttons
        const baseClasses = clsx(
            'focus:outline-none',
            sizeClasses[size],
            iconSizeClasses[size]
        );

        // Variant and color specific classes
        const getVariantClasses = () => {
            if (variant === 'ghost') {
                const ghostColorMap = {
                    primary: 'text-gray-700',
                    secondary: 'text-gray-600',
                    success: 'text-red-600',
                    error: 'text-red-600'
                };

                return clsx(
                    'bg-transparent',
                    ghostColorMap[color],
                    !isDisabled && isHovered && 'bg-gray-100',
                    !isDisabled && isPressed && 'bg-gray-200'
                );
            }

            if (variant === 'outlined') {
                const outlinedColorMap = {
                    primary: 'text-gray-900 border-gray-300',
                    secondary: 'text-gray-700 border-gray-300',
                    success: 'text-red-600 border-red-300',
                    error: 'text-red-600 border-red-300'
                };

                return clsx(
                    'border bg-white',
                    outlinedColorMap[color],
                    'shadow-sm',
                    !isDisabled && isHovered && 'bg-gray-50',
                    !isDisabled && isPressed && 'bg-gray-100'
                );
            }

            if (variant === 'contained') {
                const containedColorMap = {
                    primary: 'bg-gray-900 text-white',
                    secondary: 'bg-gray-200 text-gray-900',
                    success: 'bg-green-300 text-white',
                    error: 'bg-red-300 text-white'
                };

                const containedHoverMap = {
                    primary: 'bg-gray-800',
                    secondary: 'bg-gray-300',
                    success: 'bg-green-400',
                    error: 'bg-red-400'
                };

                return clsx(
                    containedColorMap[color],
                    !isDisabled && isHovered && containedHoverMap[color],
                    !isDisabled && isPressed && containedHoverMap[color]
                );
            }

            if (variant === 'embedded') {
                const embeddedColorMap = {
                    primary: 'text-gray-900',
                    secondary: 'text-gray-700',
                    success: 'text-red-600',
                    error: 'text-red-600'
                };

                return clsx(
                    'bg-transparent',
                    embeddedColorMap[color],
                    !isDisabled && isHovered && 'bg-gray-100',
                    !isDisabled && isPressed && 'bg-gray-150'
                );
            }
        };

        const focusClasses = isFocusVisible && !isDisabled
            ? 'ring-2 ring-pink-500 ring-offset-2'
            : '';

        return (
            <button
                {...buttonProps}
                {...hoverProps}
                {...focusProps}
                aria-busy={loading || undefined}
                ref={internalRef}
                className={clsx(
                    'btn-base',
                    baseClasses,
                    getVariantClasses(),
                    focusClasses,
                    {
                        'btn-pressed': isPressed && !isDisabled,
                        'btn-disabled': isDisabled
                    },
                    className
                )}
            >
                {loading ? (
                    <div className="flex items-center justify-center">
                        <div
                            className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                    </div>
                ) : (
                    <div className="flex items-center gap-2">
                        {children}
                    </div>
                )}
            </button>
        );
    }
);

IconButton.displayName = 'IconButton';